<template>
  <form class="sm:flex align-middle justify-center pt-2 sm:pt-0 pb-2">
    <AmountInput
      class="flex-1 md:px-6 pb-3 md:pt-6 md:min-w-0"
      wrapper-class="focus-within:border-brand-600"
      :label="$t('exchange_rate_search.from')"
      :header-text="$t('exchange_rate_search.choose_currency')"
      :currencies="labeledCurrencies"
      :amount-value="amountFrom"
      :currency-value="currencyFrom"
      show-currency-flag
      allow-decimals
      autofocus
      @input-amount="handleAmountFrom($event)"
      @input-currency="handleChangeCurrency('FROM', $event)"
      currency-filter
    />
    <a
      :href="
        getCurrencyUrl({
          currencyFrom: { iso: currencyTo },
          currencyTo: { iso: currencyFrom },
        })
      "
      class="flex items-center shrink-0 w-6 h-6 self-center sm:mt-8 mt-6 sm:mx-5 mb-2 sm:mb-0 mx-auto cursor-pointer"
    >
      <svg role="presentation" focusable="false" tabindex="-1" class="rotate-90 sm:rotate-0"
         
        alt="switch currencies"
        svg-inline
       xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32"><title>switch currencies</title><path fill="currentColor" fill-rule="evenodd" d="m21.611 6.382 4 3.908c.25.246.389.577.389.923 0 .345-.14.677-.389.922l-4 3.906a1.337 1.337 0 0 1-.942.382 1.361 1.361 0 0 1-.943-.381 1.3 1.3 0 0 1-.39-.922c0-.345.14-.677.39-.921l1.724-1.686H8.667a1.348 1.348 0 0 1-.916-.395 1.288 1.288 0 0 1-.377-.907c0-.339.135-.664.377-.908.241-.243.57-.384.916-.395H21.45l-1.724-1.684a1.288 1.288 0 0 1-.39-.922c0-.345.14-.676.391-.92.25-.245.59-.382.943-.382.354 0 .693.137.942.382ZM10.388 25.618l-4-3.908A1.295 1.295 0 0 1 6 20.787c0-.345.14-.677.389-.922l4-3.908a1.336 1.336 0 0 1 .943-.381 1.36 1.36 0 0 1 .943.382 1.3 1.3 0 0 1 .39.922 1.276 1.276 0 0 1-.392.921l-1.723 1.683H23.33c.354 0 .693.137.944.382.25.244.39.576.39.921 0 .346-.14.678-.39.922a1.35 1.35 0 0 1-.944.382H10.55l1.725 1.685a1.301 1.301 0 0 1 .39.922 1.277 1.277 0 0 1-.391.92 1.335 1.335 0 0 1-.943.382 1.362 1.362 0 0 1-.942-.382Z" clip-rule="evenodd"/></svg>
    </a>
    <AmountInput
      class="flex-1 md:px-6 pb-3 md:pt-6 md:min-w-0"
      wrapper-class="focus-within:border-brand-600"
      :label="$t('exchange_rate_search.to')"
      :header-text="$t('exchange_rate_search.choose_currency')"
      :currencies="labeledCurrencies"
      :amount-value="amountTo"
      :currency-value="currencyTo"
      show-currency-flag
      allow-decimals
      @input-amount="handleAmountTo($event)"
      @input-currency="handleChangeCurrency('TO', $event)"
      currency-filter
    />
  </form>
</template>

<script lang="ts" setup>
const props = defineProps<{
  defaultCurrencyFrom: string
  defaultCurrencyTo: string
  defaultAmount: number
  liveRate: number | null
}>()

const { $masterData } = useNuxtApp()

const currencyFrom = ref(props.defaultCurrencyFrom)
const currencyTo = ref(props.defaultCurrencyTo)
const amountFrom = ref(props.defaultAmount)
const amountTo = ref(0)
const exchangeRate = ref(props.liveRate)

const calculatedAmountTo = computed(() => {
  const count = props.liveRate || exchangeRate.value || 1
  return Number((amountFrom.value * count).toFixed(2))
})

watch(
  () => props.liveRate,
  (newLiveRate) => {
    if (newLiveRate) {
      exchangeRate.value = props.liveRate
      amountTo.value = calculatedAmountTo.value
    }
  },
  { immediate: true },
)

watch(
  () => props.defaultAmount,
  (newDefaultAmount) => {
    if (newDefaultAmount) {
      amountTo.value = calculatedAmountTo.value
    }
  },
  { immediate: true },
)

function handleAmountFrom(amount: number) {
  amountFrom.value = amount
  if (exchangeRate.value) {
    amountTo.value = Number((amount * exchangeRate.value).toFixed(2))
  }
}

function handleAmountTo(amount: number) {
  amountTo.value = amount
  if (exchangeRate.value) {
    amountFrom.value = Number((amount / exchangeRate.value).toFixed(2))
  }
}

async function handleChangeCurrency(type: 'FROM' | 'TO', currency: string) {
  await navigate(
    getCurrencyUrl({
      currencyFrom: { iso: type === 'FROM' ? currency : currencyFrom.value },
      currencyTo: { iso: type === 'TO' ? currency : currencyTo.value },
    }),
  )
}

const labeledCurrencies = computed(() => {
  return Array.from($masterData.currencies.values()).map(({ iso, labels }) => ({
    iso,
    label: labels.singular,
  }))
})
</script>
